// API client for RUTINEO

const API_BASE = import.meta.env.VITE_API_URL || '';

// Token storage para autenticación
let authToken: string | null = null;

// Callback para refrescar el token cuando sea necesario
let refreshTokenCallback: (() => Promise<string | null>) | null = null;

// Callback para disparar evento de sesión expirada
let sessionExpiredCallback: (() => void) | null = null;

// Función para configurar el token (llamar desde el Auth provider)
export function setAuthToken(token: string | null) {
  authToken = token;
}

// Función para obtener el token actual
export function getAuthToken(): string | null {
  return authToken;
}

// Función para configurar el callback de refresh
export function setRefreshTokenCallback(callback: () => Promise<string | null>) {
  refreshTokenCallback = callback;
}

// Función para configurar el callback de sesión expirada
export function setSessionExpiredCallback(callback: () => void) {
  sessionExpiredCallback = callback;
}

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Agregar token de autenticación si existe
  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }

  const config: RequestInit = {
    ...options,
    headers,
  };

  const response = await fetch(url, config);

  // Manejar error 401 (Unauthorized) - Token expirado o inválido
  if (response.status === 401) {
    // Si ya intentamos refrescar una vez, no intentar de nuevo
    if (retryCount > 0) {
      // Disparar evento de sesión expirada
      if (sessionExpiredCallback) {
        sessionExpiredCallback();
      }
      const error = await response.json().catch(() => ({ message: 'Sesión expirada' }));
      throw new Error(error.message || error.error || 'Sesión expirada. Por favor, inicia sesión nuevamente.');
    }

    // Intentar refrescar el token
    if (refreshTokenCallback) {
      try {
        const newToken = await refreshTokenCallback();
        if (newToken) {
          // Actualizar el token y reintentar la petición
          authToken = newToken;
          // Reintentar la petición con el nuevo token
          return fetchAPI<T>(endpoint, options, retryCount + 1);
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
        // Si el refresh falla, disparar evento de sesión expirada
        if (sessionExpiredCallback) {
          sessionExpiredCallback();
        }
        throw new Error('No se pudo renovar la sesión. Por favor, inicia sesión nuevamente.');
      }
    }

    // Si no hay callback de refresh o falló, disparar evento de sesión expirada
    if (sessionExpiredCallback) {
      sessionExpiredCallback();
    }
    const error = await response.json().catch(() => ({ message: 'Sesión expirada' }));
    throw new Error(error.message || error.error || 'Sesión expirada. Por favor, inicia sesión nuevamente.');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: 'Error desconocido' }));
    // Preservar status y code para que la UI pueda reaccionar (ej: 409 already_assigned).
    const err = new Error(body.message || body.error || `Error ${response.status}`) as Error & {
      status?: number;
      code?: string;
    };
    err.status = response.status;
    err.code = body.code;
    throw err;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// Students API
export const studentsAPI = {
  getAll: (params?: { search?: string; active?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.active !== undefined) searchParams.set('active', String(params.active));
    const query = searchParams.toString();
    return fetchAPI<any[]>(`/api/users/students${query ? `?${query}` : ''}`);
  },
  
  getById: (id: string) => fetchAPI<any>(`/api/users/students/${id}`),
  
  create: (data: any) => fetchAPI<any>('/api/users/students', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: string, data: any) => fetchAPI<any>(`/api/users/students/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: string) => fetchAPI<void>(`/api/users/students/${id}`, {
    method: 'DELETE',
  }),
};

// Rendimiento registrado por el alumno (series con peso/reps/RPE)
export const trackingAPI = {
  // Historial de series logueadas del alumno, scopeado a los planes del entrenador.
  getHistory: (studentId: string, exerciseId?: string) => {
    const q = new URLSearchParams({ studentId });
    if (exerciseId) q.set('exerciseId', exerciseId);
    return fetchAPI<any[]>(`/api/tracking/history?${q.toString()}`);
  },
};

// Transferencias de alumnos entre entrenadores
export const transfersAPI = {
  // Solicitar quedarse con un alumno que ya tiene otro entrenador (lo confirma el alumno).
  request: (email: string) =>
    fetchAPI<any>('/api/transfers', { method: 'POST', body: JSON.stringify({ email }) }),

  // Solicitudes enviadas por el entrenador (pendientes; ?all=1 = historial).
  list: (all?: boolean) => fetchAPI<any[]>(`/api/transfers${all ? '?all=1' : ''}`),

  cancel: (id: string) => fetchAPI<void>(`/api/transfers/${id}/cancel`, { method: 'POST' }),
};

// Users API
export const usersAPI = {
  getMe: () => fetchAPI<any>('/api/users/me'),

  updateMe: (data: any) => fetchAPI<any>('/api/users/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  getMyTrainer: () => fetchAPI<any>('/api/users/my-trainer'),

  // Genera (o regenera) el link de ficha de alta para que el atleta la complete.
  createIntakeLink: (studentId: string) =>
    fetchAPI<{ token: string; expiresAt: string }>(`/api/users/students/${studentId}/intake-link`, {
      method: 'POST',
    }),
};

// Intake público (sin login): el atleta completa su ficha con un token.
export const intakeAPI = {
  get: (token: string) => fetchAPI<any>(`/api/intake/${token}`),
  submit: (token: string, data: any) =>
    fetchAPI<any>(`/api/intake/${token}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params?: { unread?: boolean; limit?: number }) => {
    const sp = new URLSearchParams();
    if (params?.unread) sp.set('unread', 'true');
    if (params?.limit) sp.set('limit', String(params.limit));
    const q = sp.toString();
    return fetchAPI<any[]>(`/api/notifications${q ? `?${q}` : ''}`);
  },

  getUnreadCount: () => fetchAPI<{ count: number }>('/api/notifications/unread-count'),

  markRead: (id: string) =>
    fetchAPI<any>(`/api/notifications/${id}/read`, { method: 'POST' }),

  markAllRead: () => fetchAPI<any>('/api/notifications/read-all', { method: 'POST' }),

  subscribe: (data: { platform: 'web'; endpoint: string; keys: { p256dh: string; auth: string } }) =>
    fetchAPI<any>('/api/notifications/subscriptions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  unsubscribe: (endpoint: string) =>
    fetchAPI<void>('/api/notifications/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
};

// Venues API (sedes de entrenamiento)
export const venuesAPI = {
  getAll: () => fetchAPI<any[]>('/api/venues'),

  create: (data: { name: string; address?: string; notes?: string }) =>
    fetchAPI<any>('/api/venues', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: { name?: string; address?: string; notes?: string }) =>
    fetchAPI<any>(`/api/venues/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchAPI<void>(`/api/venues/${id}`, {
      method: 'DELETE',
    }),
};

// Plans API
export const plansAPI = {
  getAll: (params?: { studentId?: string; active?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.studentId) searchParams.set('studentId', params.studentId);
    if (params?.active !== undefined) searchParams.set('active', String(params.active));
    const query = searchParams.toString();
    return fetchAPI<any[]>(`/api/plans${query ? `?${query}` : ''}`);
  },
  
  getById: (id: string) => fetchAPI<any>(`/api/plans/${id}`),
  
  create: (data: any) => fetchAPI<any>('/api/plans', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: string, data: any) => fetchAPI<any>(`/api/plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: string) => fetchAPI<void>(`/api/plans/${id}`, {
    method: 'DELETE',
  }),
  
  duplicate: (id: string, data: { newStudentId?: string; newName?: string }) =>
    fetchAPI<any>(`/api/plans/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Weeks API
export const weeksAPI = {
  getByPlan: (planId: string) => fetchAPI<any[]>(`/api/weeks?planId=${planId}`),
  
  getById: (id: string) => fetchAPI<any>(`/api/weeks/${id}`),
  
  create: (data: any) => fetchAPI<any>('/api/weeks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: string, data: any) => fetchAPI<any>(`/api/weeks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: string) => fetchAPI<void>(`/api/weeks/${id}`, {
    method: 'DELETE',
  }),
  
  duplicate: (id: string, data: { targetPlanId?: string; newWeekNumber?: number }) =>
    fetchAPI<any>(`/api/weeks/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Days API
export const daysAPI = {
  getByWeek: (weekId: string) => fetchAPI<any[]>(`/api/days?weekId=${weekId}`),
  
  getById: (id: string) => fetchAPI<any>(`/api/days/${id}`),
  
  create: (data: any) => fetchAPI<any>('/api/days', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: string, data: any) => fetchAPI<any>(`/api/days/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: string) => fetchAPI<void>(`/api/days/${id}`, {
    method: 'DELETE',
  }),

  duplicate: (id: string, data: { targetWeekId?: string; newDayNumber?: number }) =>
    fetchAPI<any>(`/api/days/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Blocks API
export const blocksAPI = {
  getByDay: (dayId: string) => fetchAPI<any[]>(`/api/blocks?dayId=${dayId}`),
  
  getById: (id: string) => fetchAPI<any>(`/api/blocks/${id}`),
  
  create: (data: any) => fetchAPI<any>('/api/blocks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: string, data: any) => fetchAPI<any>(`/api/blocks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: string) => fetchAPI<void>(`/api/blocks/${id}`, {
    method: 'DELETE',
  }),
  
  addItem: (blockId: string, data: any) => fetchAPI<any>(`/api/blocks/${blockId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateItem: (blockId: string, itemId: string, data: any) =>
    fetchAPI<any>(`/api/blocks/${blockId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteItem: (blockId: string, itemId: string) =>
    fetchAPI<void>(`/api/blocks/${blockId}/items/${itemId}`, {
      method: 'DELETE',
    }),
  
  reorderItems: (blockId: string, itemIds: string[]) =>
    fetchAPI<any>(`/api/blocks/${blockId}/items/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ itemIds }),
    }),

  reorder: (params: { dayId: string; parentBlockId?: string | null; blockIds: string[] }) =>
    fetchAPI<any>('/api/blocks/reorder', {
      method: 'PUT',
      body: JSON.stringify(params),
    }),
};

// Exercises API
export const exercisesAPI = {
  getAll: (params?: { search?: string; muscleGroup?: string; category?: string; equipment?: string; custom?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.muscleGroup) searchParams.set('muscleGroup', params.muscleGroup);
    if (params?.category) searchParams.set('category', params.category);
    if (params?.equipment) searchParams.set('equipment', params.equipment);
    if (params?.custom !== undefined) searchParams.set('custom', String(params.custom));
    const query = searchParams.toString();
    return fetchAPI<any[]>(`/api/exercises${query ? `?${query}` : ''}`);
  },
  
  getGrouped: () => fetchAPI<any>('/api/exercises/grouped'),
  
  getFilters: () => fetchAPI<any>('/api/exercises/filters'),
  
  getById: (id: string) => fetchAPI<any>(`/api/exercises/${id}`),
  
  create: (data: any) => fetchAPI<any>('/api/exercises', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: string, data: any) => fetchAPI<any>(`/api/exercises/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: string) => fetchAPI<void>(`/api/exercises/${id}`, {
    method: 'DELETE',
  }),

  // Pisar video/imagen de un ejercicio GLOBAL para el entrenador y sus alumnos.
  setOverride: (id: string, data: { videoUrl?: string; imageUrl?: string }) =>
    fetchAPI<any>(`/api/exercises/${id}/override`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Quitar el override (vuelve al valor del catálogo global).
  deleteOverride: (id: string) => fetchAPI<void>(`/api/exercises/${id}/override`, {
    method: 'DELETE',
  }),
};

// Comments API
export const commentsAPI = {
  getAll: (params?: { studentId?: string; weekId?: string; blockItemId?: string; type?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.studentId) searchParams.set('studentId', params.studentId);
    if (params?.weekId) searchParams.set('weekId', params.weekId);
    if (params?.blockItemId) searchParams.set('blockItemId', params.blockItemId);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return fetchAPI<any[]>(`/api/comments${query ? `?${query}` : ''}`);
  },
  
  getRecent: () => fetchAPI<any[]>('/api/comments/recent'),

  // Bandeja de conversaciones del entrenador (un hilo por alumno).
  getConversations: () => fetchAPI<any[]>('/api/comments/conversations'),

  // Hilo completo con un alumno (marca como leídos los entrantes).
  getThread: (studentId: string) => fetchAPI<any[]>(`/api/comments/thread/${studentId}`),

  getById: (id: string) => fetchAPI<any>(`/api/comments/${id}`),
  
  getStats: (studentId: string) => fetchAPI<any>(`/api/comments/stats/${studentId}`),
  
  create: (data: any) => fetchAPI<any>('/api/comments', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id: string, data: any) => fetchAPI<any>(`/api/comments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id: string) => fetchAPI<void>(`/api/comments/${id}`, {
    method: 'DELETE',
  }),
};
